import { useState, useRef } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';

interface PremiumButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'ghost';
  children: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  'aria-label'?: string;
}

export default function PremiumButton({
  variant = 'primary',
  children,
  className = '',
  isLoading = false,
  'aria-label': ariaLabel,
  ...props
}: PremiumButtonProps) {
  const [ripples, setRipples] = useState<Array<{ x: number; y: number; id: number }>>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const id = Date.now();
      setRipples(prev => [...prev, { x, y, id }]);

      setTimeout(() => {
        setRipples(prev => prev.filter(ripple => ripple.id !== id));
      }, 600);
    }

    if (props.onClick && !props.disabled) {
      props.onClick(e);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const variants = {
    primary: {
      base: "bg-[#d4a10d] text-white font-semibold relative overflow-hidden",
      hover: "hover:bg-[#b8940b]",
      border: "border-2 border-transparent hover:border-[#FFD700]"
    },
    secondary: {
      base: "bg-white text-gray-900 font-semibold relative overflow-hidden border-2 border-gray-200",
      hover: "hover:bg-gray-50 hover:border-[#d4a10d]",
      border: ""
    },
    ghost: {
      base: "bg-transparent text-gray-900 font-semibold relative overflow-hidden border-2 border-gray-300",
      hover: "hover:bg-gray-50 hover:border-[#d4a10d]",
      border: ""
    }
  };

  const selectedVariant = variants[variant];

  return (
    <motion.button
      {...props}
      ref={buttonRef}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      disabled={props.disabled || isLoading}
      aria-label={ariaLabel}
      className={`
        ${selectedVariant.base}
        ${selectedVariant.hover}
        ${selectedVariant.border}
        px-8 py-4 rounded-lg
        transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed
        shadow-lg hover:shadow-xl
        ${className}
      `}
    >
      {variant === 'primary' && (
        <>
          <div
            className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(255, 215, 0, 0.15), transparent 50%)`
            }}
          />

          <div className="absolute inset-0 opacity-0 hover:opacity-100">
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.4), transparent)',
                animation: 'shimmer 2s infinite',
              }}
            />
          </div>
        </>
      )}

      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-white/30 pointer-events-none"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: '4px',
            height: '4px',
            animation: 'ripple 0.6s ease-out'
          }}
        />
      ))}

      <span className="relative z-10 flex items-center justify-center gap-2">
        {isLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Loading...</span>
          </>
        ) : (
          children
        )}
      </span>

      {isLoading && variant === 'primary' && (
        <div className="absolute bottom-0 left-0 h-1 bg-[#FFD700] animate-loading-progress" />
      )}
    </motion.button>
  );
}
